import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  MenuItem,
  ListItemText,
  Paper,
  Radio,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, Delete, Search } from '@mui/icons-material';
import getOrganizationById from 'redux/actions/Common/getOrganizationById';
import getSimulationToolsCatalog from 'redux/actions/Common/getSimulationToolsCatalog';
import getPreviousBuyers from 'redux/actions/Common/getPreviousBuyers';
import createSimulation from 'redux/actions/Common/createSimulation';
import ToolsPicker, { ALWAYS_ON_TOOLS } from './ToolsPicker';
import useOrgSearch from './useOrgSearch';
import {
  DEFAULT_SIM_DAY_MINUTES,
  SIM_DAY_MINUTE_OPTIONS,
  DEFAULT_SIMULATED_START_OFFSET_DAYS,
  LLM_PROVIDERS,
  DEFAULT_LLM_PROVIDER,
  DEFAULT_LLM_MODEL,
  llmProviderLabel,
  modelOptionsForProvider,
  TICK_PROMPT_PLACEHOLDER_HELP,
  DEFAULT_SELLER_TICK_PROMPT_TEMPLATE,
  DEFAULT_BUYER_TICK_PROMPT_TEMPLATE,
  renderTickPromptPreview,
  formatSimDaySpeed,
  minutesToMs,
  previousBuyerToFormValues,
} from './simulationUtils';
import moment from 'moment';
import BuyerContactFields, {
  EMPTY_CONTACT,
  buyerContactSchema,
  toBuyerContactPayload,
} from './BuyerContactFields';

const STEPS = ['Seller Org', 'Seller', 'Buyer', 'Tools', 'Review'];

const initialValues = {
  companyWebsite: '',
  companyLinkedInUrl: '',
  buyerContacts: [{ ...EMPTY_CONTACT }],
  sellerPersonaOverride: '',
  tickIntervalSeconds: 90,
  simDayMinutes: DEFAULT_SIM_DAY_MINUTES,
  simulatedStartOffsetDays: DEFAULT_SIMULATED_START_OFFSET_DAYS,
  buyerNonResponsePercent: 0,
  llmProvider: DEFAULT_LLM_PROVIDER,
  llmModel: DEFAULT_LLM_MODEL,
  sellerTickPromptTemplate: DEFAULT_SELLER_TICK_PROMPT_TEMPLATE,
  buyerTickPromptTemplate: DEFAULT_BUYER_TICK_PROMPT_TEMPLATE,
};

// Best-effort, display-only label for the tick-prompt preview — the real
// buyer company name is resolved later by the seller agent's own research
// (scraping companyWebsite), not known at creation time, so this just
// strips the URL down to something readable in the meantime.
const websiteToCompanyLabel = (website) =>
  (website || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');

const validationSchema = yup.object({
  companyWebsite: yup
    .string()
    .trim()
    .url('Enter a valid URL, e.g. https://acme.com')
    .required('Company website is required'),
  companyLinkedInUrl: yup
    .string()
    .trim()
    .url('Enter a valid URL, e.g. https://linkedin.com/company/acme')
    .required('Company LinkedIn URL is required'),
  buyerContacts: yup
    .array()
    .of(buyerContactSchema)
    .min(1, 'Add at least one buyer contact'),
  sellerPersonaOverride: yup.string(),
  tickIntervalSeconds: yup
    .number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .moreThan(0, 'Must be greater than 0')
    .required('Tick interval is required'),
  buyerNonResponsePercent: yup
    .number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Must be between 0 and 100')
    .max(100, 'Must be between 0 and 100'),
  simulatedStartOffsetDays: yup
    .number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Must be 0 or greater'),
  llmProvider: yup
    .string()
    .oneOf(LLM_PROVIDERS, 'Pick a supported provider')
    .required('LLM provider is required'),
  llmModel: yup.string().trim().required('Model is required'),
});

const SelectableRow = ({ selected, onClick, primary, secondary }) => (
  <ListItemButton selected={selected} onClick={onClick} dense>
    <Radio size="small" checked={selected} sx={{ p: 0.5, mr: 1 }} />
    <ListItemText
      primary={primary}
      secondary={secondary}
      primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
      secondaryTypographyProps={{ variant: 'caption' }}
    />
  </ListItemButton>
);

const ReviewRow = ({ label, children }) => (
  <Box display={'flex'} gap={2} py={0.75}>
    <Typography
      variant={'body2'}
      color={'text.secondary'}
      sx={{ minWidth: 160 }}
    >
      {label}
    </Typography>
    <Box flex={1} sx={{ wordBreak: 'break-word' }}>
      {children}
    </Box>
  </Box>
);

const CreateSimulationDialog = ({ open, onClose, onCreated }) => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 — organization
  const orgSearch = useOrgSearch({ enabled: open });
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Step 2 — seller user (only users of the selected org)
  const [orgUsers, setOrgUsers] = useState([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userKeyword, setUserKeyword] = useState('');
  const [sellerUser, setSellerUser] = useState(null);

  // Step 3 — buyer (reuse-a-previous-buyer picker)
  const [previousBuyers, setPreviousBuyers] = useState([]);
  const [fetchingPreviousBuyers, setFetchingPreviousBuyers] = useState(false);
  const [selectedPreviousBuyer, setSelectedPreviousBuyer] = useState(null);

  // Step 4 — tools
  const [tools, setTools] = useState([]);
  const [fetchingTools, setFetchingTools] = useState(false);
  const [toolsError, setToolsError] = useState(null);
  const [restrictTools, setRestrictTools] = useState(false);
  const [selectedTools, setSelectedTools] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });

  const resetAll = () => {
    setActiveStep(0);
    orgSearch.setKeyword('');
    setSelectedOrg(null);
    setOrgUsers([]);
    setUsersError(null);
    setUserKeyword('');
    setSellerUser(null);
    setPreviousBuyers([]);
    setFetchingPreviousBuyers(false);
    setSelectedPreviousBuyer(null);
    setRestrictTools(false);
    setSelectedTools([]);
    setSubmitError(null);
    formik.resetForm();
  };

  const handleClose = () => {
    if (submitting) return;
    resetAll();
    onClose();
  };

  // Tools catalog only changes when a tool is added on the backend — fetch once.
  useEffect(() => {
    if (!open || tools.length > 0 || fetchingTools) return;
    setFetchingTools(true);
    setToolsError(null);
    dispatch(
      getSimulationToolsCatalog(
        (data) => {
          setTools(data || []);
          setFetchingTools(false);
        },
        (message) => {
          setToolsError(message);
          setFetchingTools(false);
        },
      ),
    );
  }, [open]);

  const fetchOrgUsers = (orgId) => {
    setFetchingUsers(true);
    setUsersError(null);
    setOrgUsers([]);
    dispatch(
      getOrganizationById(
        orgId,
        { fetchUsers: true, fetchTeams: false },
        (data) => {
          setOrgUsers(data?.customFields?.users || []);
          setFetchingUsers(false);
        },
        (message) => {
          setUsersError(message);
          setFetchingUsers(false);
        },
      ),
    );
  };

  const fetchPreviousBuyers = (orgId) => {
    setFetchingPreviousBuyers(true);
    setPreviousBuyers([]);
    dispatch(
      getPreviousBuyers(
        orgId,
        (data) => {
          setPreviousBuyers(data || []);
          setFetchingPreviousBuyers(false);
        },
        () => {
          // Best-effort: the picker just stays empty on failure — the admin can still
          // enter a new buyer from scratch, same as before this feature existed.
          setFetchingPreviousBuyers(false);
        },
      ),
    );
  };

  const handleSelectOrg = (org) => {
    if (selectedOrg?.id === org?.id) return;
    setSelectedOrg(org);
    // A seller must belong to the selected org.
    setSellerUser(null);
    setUserKeyword('');
    setSelectedPreviousBuyer(null);
    fetchOrgUsers(org?.id);
    fetchPreviousBuyers(org?.id);
  };

  const validateBuyer = async () => {
    const errors = await formik.validateForm();
    formik.setTouched(
      {
        companyWebsite: true,
        companyLinkedInUrl: true,
        tickIntervalSeconds: true,
        llmProvider: true,
        llmModel: true,
        buyerContacts: formik.values.buyerContacts.map(() => ({
          name: true,
          title: true,
          persona: true,
          email: true,
        })),
      },
      false,
    );
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 2 && !(await validateBuyer())) return;
    setActiveStep((step) => step + 1);
  };

  const canGoNext =
    (activeStep === 0 && Boolean(selectedOrg)) ||
    (activeStep === 1 && Boolean(sellerUser)) ||
    activeStep === 2 ||
    (activeStep === 3 && !(restrictTools && (fetchingTools || toolsError)));

  const buildPayload = () => {
    const values = formik.values;
    const persona = values.sellerPersonaOverride?.trim();
    return {
      organizationId: selectedOrg?.id,
      companyWebsite: values.companyWebsite.trim(),
      companyLinkedInUrl: values.companyLinkedInUrl.trim(),
      sellerUserId: sellerUser?.id,
      buyerContacts: values.buyerContacts.map(toBuyerContactPayload),
      ...(persona ? { sellerPersonaOverride: persona } : {}),
      tickIntervalSeconds: Number(values.tickIntervalSeconds),
      // Picked in minutes; the backend expects milliseconds.
      realMsPerSimDay: minutesToMs(values.simDayMinutes),
      simulatedStartOffsetDays: Number(values.simulatedStartOffsetDays) || 0,
      buyerNonResponsePercent: Number(values.buyerNonResponsePercent) || 0,
      llmProvider: values.llmProvider,
      llmModel: values.llmModel,
      // Blank means the built-in default, so omit rather than send an empty override.
      ...(values.sellerTickPromptTemplate?.trim()
        ? { sellerTickPromptTemplate: values.sellerTickPromptTemplate.trim() }
        : {}),
      ...(values.buyerTickPromptTemplate?.trim()
        ? { buyerTickPromptTemplate: values.buyerTickPromptTemplate.trim() }
        : {}),
      // An empty list means unrestricted on the backend, so a restricted
      // selection always carries the setup tools to stay non-empty.
      enabledTools: restrictTools
        ? Array.from(new Set([...ALWAYS_ON_TOOLS, ...selectedTools]))
        : [],
    };
  };

  const handleSubmit = () => {
    setSubmitting(true);
    setSubmitError(null);
    dispatch(
      createSimulation(
        buildPayload(),
        (data) => {
          setSubmitting(false);
          resetAll();
          onCreated(data);
        },
        (message) => {
          setSubmitting(false);
          setSubmitError(message);
        },
      ),
    );
  };

  // Changing provider must not leave a stale model from the old provider
  // selected — jump to that provider's first (and, for minimax, intended
  // default) model.
  const handleProviderChange = (e) => {
    const provider = e.target.value;
    formik.setFieldValue('llmProvider', provider);
    const options = modelOptionsForProvider(provider);
    formik.setFieldValue(
      'llmModel',
      provider === DEFAULT_LLM_PROVIDER
        ? DEFAULT_LLM_MODEL
        : options[0]?.value || '',
    );
  };

  const setContact = (index, field, value) =>
    formik.setFieldValue(`buyerContacts[${index}].${field}`, value);

  const contactError = (index, field) =>
    formik.touched.buyerContacts?.[index]?.[field] &&
    formik.errors.buyerContacts?.[index]?.[field];

  const trimmedUserKeyword = userKeyword.trim().toLowerCase();
  const filteredUsers = orgUsers.filter(
    (user) =>
      !trimmedUserKeyword ||
      user?.fullName?.toLowerCase()?.includes(trimmedUserKeyword) ||
      user?.email?.toLowerCase()?.includes(trimmedUserKeyword),
  );

  const renderOrgStep = () => (
    <Box display={'flex'} flexDirection={'column'} gap={1.5}>
      <TextField
        size="small"
        autoFocus
        placeholder="Search organizations..."
        value={orgSearch.keyword}
        onChange={(e) => orgSearch.setKeyword(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: orgSearch.fetching ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null,
        }}
      />
      {selectedOrg ? (
        <Typography variant={'caption'} color={'text.secondary'}>
          Selected: <b>{selectedOrg?.organizationName || selectedOrg?.id}</b>
        </Typography>
      ) : null}
      {orgSearch.error ? (
        <Alert severity="error">{orgSearch.error}</Alert>
      ) : null}
      <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
        {!orgSearch.fetching && orgSearch.organizations.length === 0 ? (
          <Typography
            variant={'body2'}
            color={'text.secondary'}
            textAlign={'center'}
            py={4}
          >
            No organizations found
          </Typography>
        ) : (
          <List dense disablePadding>
            {orgSearch.organizations.map((org) => (
              <SelectableRow
                key={org?.id}
                selected={selectedOrg?.id === org?.id}
                onClick={() => handleSelectOrg(org)}
                primary={org?.organizationName || '--'}
                secondary={org?.id}
              />
            ))}
          </List>
        )}
        {orgSearch.hasMore ? (
          <Box display={'flex'} justifyContent={'center'} p={1}>
            <Button
              size="small"
              onClick={orgSearch.loadMore}
              disabled={orgSearch.fetchingMore}
            >
              {orgSearch.fetchingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );

  const renderSellerStep = () => (
    <Box display={'flex'} flexDirection={'column'} gap={1.5}>
      <Typography variant={'body2'} color={'text.secondary'}>
        The seller agent acts through this user’s real account in{' '}
        <b>{selectedOrg?.organizationName || selectedOrg?.id}</b>.
      </Typography>
      <TextField
        size="small"
        placeholder="Filter users by name or email..."
        value={userKeyword}
        onChange={(e) => setUserKeyword(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      {usersError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchOrgUsers(selectedOrg?.id)}
            >
              Retry
            </Button>
          }
        >
          {usersError}
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
        {fetchingUsers ? (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={4}
          >
            <CircularProgress size={20} />
            <Typography variant={'body2'} color={'text.secondary'}>
              Loading users...
            </Typography>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography
            variant={'body2'}
            color={'text.secondary'}
            textAlign={'center'}
            py={4}
          >
            {orgUsers.length === 0
              ? 'This organization has no users'
              : 'No users match this filter'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filteredUsers.map((user) => (
              <SelectableRow
                key={user?.id}
                selected={sellerUser?.id === user?.id}
                onClick={() => setSellerUser(user)}
                primary={user?.fullName || user?.email || user?.id}
                secondary={user?.fullName ? user?.email : user?.id}
              />
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );

  // Live, read-only preview of each tick-prompt template using the buyer/
  // seller info already entered elsewhere in this same step (and the seller
  // picked in the previous step) — purely illustrative, recomputed on every
  // keystroke; what actually gets sent is the raw template text above it.
  const tickPromptPreviewValues = {
    sellerName: sellerUser?.fullName || sellerUser?.email || '',
    sellerCompany: selectedOrg?.organizationName || '',
    buyerNames: formik.values.buyerContacts
      .map((c) => c.name?.trim())
      .filter(Boolean)
      .join(', '),
    buyerCompany: websiteToCompanyLabel(formik.values.companyWebsite),
  };

  const handleSelectPreviousBuyer = (buyer) => {
    setSelectedPreviousBuyer(buyer);
    formik.setValues({
      ...formik.values,
      ...previousBuyerToFormValues(buyer),
    });
  };

  const renderBuyerStep = () => (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <Autocomplete
        size="small"
        options={previousBuyers}
        loading={fetchingPreviousBuyers}
        getOptionLabel={(b) =>
          `${b.buyerCompanyName || websiteToCompanyLabel(b.companyWebsite)}` +
          (b.buyerContacts?.[0]?.name ? ` — ${b.buyerContacts[0].name}` : '')
        }
        isOptionEqualToValue={(a, b) => a.companyWebsite === b.companyWebsite}
        value={selectedPreviousBuyer}
        onChange={(_, buyer) => handleSelectPreviousBuyer(buyer)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Reuse a previous buyer"
            placeholder="Pick a company you've tested before, or leave blank to add a new one"
          />
        )}
      />
      <Box display={'flex'} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
        <TextField
          size="small"
          fullWidth
          label="Company website *"
          placeholder="https://acme.com"
          name="companyWebsite"
          value={formik.values.companyWebsite}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.companyWebsite &&
            Boolean(formik.errors.companyWebsite)
          }
          helperText={
            (formik.touched.companyWebsite && formik.errors.companyWebsite) ||
            'The buyer company — feeds the seller’s research'
          }
        />
        <TextField
          size="small"
          fullWidth
          label="Company LinkedIn URL *"
          placeholder="https://linkedin.com/company/acme"
          name="companyLinkedInUrl"
          value={formik.values.companyLinkedInUrl}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.companyLinkedInUrl &&
            Boolean(formik.errors.companyLinkedInUrl)
          }
          helperText={
            formik.touched.companyLinkedInUrl &&
            formik.errors.companyLinkedInUrl
          }
        />
      </Box>

      <Box>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          mb={1}
        >
          <Typography variant={'subtitle2'} fontWeight={'bold'}>
            Buyer contacts ({formik.values.buyerContacts.length})
          </Typography>
          <Button
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={() =>
              formik.setFieldValue('buyerContacts', [
                ...formik.values.buyerContacts,
                { ...EMPTY_CONTACT },
              ])
            }
          >
            Add buyer
          </Button>
        </Box>
        <Box display={'flex'} flexDirection={'column'} gap={1.5}>
          {formik.values.buyerContacts.map((contact, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                mb={1}
              >
                <Typography variant={'caption'} color={'text.secondary'}>
                  Buyer #{index + 1}
                </Typography>
                <Tooltip title="Remove buyer">
                  <span>
                    <IconButton
                      size="small"
                      disabled={formik.values.buyerContacts.length === 1}
                      onClick={() =>
                        formik.setFieldValue(
                          'buyerContacts',
                          formik.values.buyerContacts.filter(
                            (_, i) => i !== index,
                          ),
                        )
                      }
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <BuyerContactFields
                contact={contact}
                onChange={(field, value) => setContact(index, field, value)}
                errorFor={(field) => contactError(index, field)}
              />
            </Paper>
          ))}
        </Box>
      </Box>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        label="Seller persona override"
        name="sellerPersonaOverride"
        value={formik.values.sellerPersonaOverride}
        onChange={formik.handleChange}
        helperText="Optional — leave blank to use the seller’s default persona"
      />
      <Box>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Seller tick prompt"
          name="sellerTickPromptTemplate"
          value={formik.values.sellerTickPromptTemplate}
          onChange={formik.handleChange}
          helperText={`Prefilled — edit freely, or clear to use the built-in default. Placeholders: ${TICK_PROMPT_PLACEHOLDER_HELP}`}
        />
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
        >
          Preview:{' '}
          {renderTickPromptPreview(
            formik.values.sellerTickPromptTemplate,
            tickPromptPreviewValues,
          )}
        </Typography>
      </Box>
      <Box>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Buyer tick prompt"
          name="buyerTickPromptTemplate"
          value={formik.values.buyerTickPromptTemplate}
          onChange={formik.handleChange}
          helperText={`Prefilled — edit freely, or clear to use the built-in default. Placeholders: ${TICK_PROMPT_PLACEHOLDER_HELP}`}
        />
        <Typography
          variant={'caption'}
          color={'text.secondary'}
          sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
        >
          Preview:{' '}
          {renderTickPromptPreview(
            formik.values.buyerTickPromptTemplate,
            tickPromptPreviewValues,
          )}
        </Typography>
      </Box>
      {/* Both fields control simulation pacing, so they sit together. */}
      <Box
        display={'flex'}
        gap={2}
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={'flex-start'}
      >
        <TextField
          size="small"
          type="number"
          label="Tick interval (seconds) *"
          name="tickIntervalSeconds"
          value={formik.values.tickIntervalSeconds}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.tickIntervalSeconds &&
            Boolean(formik.errors.tickIntervalSeconds)
          }
          helperText={
            (formik.touched.tickIntervalSeconds &&
              formik.errors.tickIntervalSeconds) ||
            'How often each agent takes a turn'
          }
          inputProps={{ min: 1 }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label="Real minutes per simulated day"
          name="simDayMinutes"
          value={formik.values.simDayMinutes}
          onChange={formik.handleChange}
          helperText="How fast the simulation’s calendar runs — fixed at creation"
          sx={{ minWidth: 260 }}
        >
          {SIM_DAY_MINUTE_OPTIONS.map((minutes) => (
            <MenuItem key={minutes} value={minutes}>
              {minutes} min
              {minutes === DEFAULT_SIM_DAY_MINUTES ? ' (default)' : ''}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Deal start date (days ago)"
          name="simulatedStartOffsetDays"
          value={formik.values.simulatedStartOffsetDays}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.simulatedStartOffsetDays &&
            Boolean(formik.errors.simulatedStartOffsetDays)
          }
          helperText={
            (formik.touched.simulatedStartOffsetDays &&
              formik.errors.simulatedStartOffsetDays) ||
            `${moment()
              .subtract(
                Number(formik.values.simulatedStartOffsetDays) || 0,
                'days',
              )
              .format('DD MMM YYYY')} — 0 means "start today"`
          }
          inputProps={{ min: 0 }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          type="number"
          label="Buyer non-response %"
          name="buyerNonResponsePercent"
          value={formik.values.buyerNonResponsePercent}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.buyerNonResponsePercent &&
            Boolean(formik.errors.buyerNonResponsePercent)
          }
          helperText={
            (formik.touched.buyerNonResponsePercent &&
              formik.errors.buyerNonResponsePercent) ||
            'Once engaged, % of ticks a buyer stays silent'
          }
          inputProps={{ min: 0, max: 100 }}
          sx={{ minWidth: 220 }}
        />
      </Box>

      <Box display={'flex'} gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
        <TextField
          select
          size="small"
          label="LLM provider"
          name="llmProvider"
          value={formik.values.llmProvider}
          onChange={handleProviderChange}
          helperText="Which backend every agent's tick calls run on"
          sx={{ minWidth: 220 }}
        >
          {LLM_PROVIDERS.map((provider) => (
            <MenuItem key={provider} value={provider}>
              {llmProviderLabel(provider)}
              {provider === DEFAULT_LLM_PROVIDER ? ' (default)' : ''}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Model"
          name="llmModel"
          value={formik.values.llmModel}
          onChange={formik.handleChange}
          error={formik.touched.llmModel && Boolean(formik.errors.llmModel)}
          helperText={formik.touched.llmModel && formik.errors.llmModel}
          sx={{ minWidth: 260 }}
        >
          {modelOptionsForProvider(formik.values.llmProvider).map(
            ({ key, value }) => (
              <MenuItem key={key} value={value}>
                {value}
              </MenuItem>
            ),
          )}
        </TextField>
      </Box>
    </Box>
  );

  const renderReviewStep = () => {
    const payload = buildPayload();
    return (
      <Box display={'flex'} flexDirection={'column'}>
        <ReviewRow label="Seller Org">
          <Typography variant={'body2'}>
            {selectedOrg?.organizationName || '--'}{' '}
            <Typography component="span" variant={'caption'}>
              ({payload.organizationId})
            </Typography>
          </Typography>
        </ReviewRow>
        <ReviewRow label="Seller">
          <Typography variant={'body2'}>
            {sellerUser?.fullName || '--'}{' '}
            <Typography component="span" variant={'caption'}>
              ({sellerUser?.email || payload.sellerUserId})
            </Typography>
          </Typography>
        </ReviewRow>
        <ReviewRow label="Company website">
          <Typography variant={'body2'}>{payload.companyWebsite}</Typography>
        </ReviewRow>
        <ReviewRow label="Company LinkedIn">
          <Typography variant={'body2'}>
            {payload.companyLinkedInUrl}
          </Typography>
        </ReviewRow>
        <ReviewRow label="Buyer contacts">
          {payload.buyerContacts.map((contact, index) => (
            <Box key={index} mb={0.75}>
              <Typography variant={'body2'} fontWeight={'bold'}>
                {contact.name} — {contact.title}
                {contact.email ? (
                  <Typography component="span" variant={'caption'}>
                    {' '}
                    ({contact.email})
                  </Typography>
                ) : null}
              </Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                {contact.persona}
              </Typography>
            </Box>
          ))}
        </ReviewRow>
        <ReviewRow label="Seller persona">
          <Typography
            variant={'body2'}
            color={
              payload.sellerPersonaOverride ? 'text.primary' : 'text.secondary'
            }
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {payload.sellerPersonaOverride || 'Default'}
          </Typography>
        </ReviewRow>
        <ReviewRow label="Tick interval">
          <Typography variant={'body2'}>
            {payload.tickIntervalSeconds}s
          </Typography>
        </ReviewRow>
        <ReviewRow label="Simulated day">
          <Typography variant={'body2'}>
            {formatSimDaySpeed(payload.realMsPerSimDay)}{' '}
            <Typography component="span" variant={'caption'}>
              (realMsPerSimDay: {payload.realMsPerSimDay})
            </Typography>
          </Typography>
        </ReviewRow>
        <ReviewRow label="Deal start date">
          <Typography variant={'body2'}>
            {payload.simulatedStartOffsetDays > 0
              ? `${moment()
                  .subtract(payload.simulatedStartOffsetDays, 'days')
                  .format('DD MMM YYYY')} (${payload.simulatedStartOffsetDays} days ago)`
              : 'Today (no backdating)'}
          </Typography>
        </ReviewRow>
        <ReviewRow label="Buyer non-response">
          <Typography variant={'body2'}>
            {payload.buyerNonResponsePercent > 0
              ? `${payload.buyerNonResponsePercent}% of ticks after first engagement`
              : 'Always responds (0%)'}
          </Typography>
        </ReviewRow>
        <ReviewRow label="LLM">
          <Typography variant={'body2'}>
            {llmProviderLabel(payload.llmProvider)}{' '}
            <Typography component="span" variant={'caption'}>
              ({payload.llmModel})
            </Typography>
          </Typography>
        </ReviewRow>
        <ReviewRow label="Tick prompts">
          <Typography variant={'body2'}>
            {(() => {
              const sellerEdited =
                Boolean(payload.sellerTickPromptTemplate) &&
                payload.sellerTickPromptTemplate !==
                  DEFAULT_SELLER_TICK_PROMPT_TEMPLATE;
              const buyerEdited =
                Boolean(payload.buyerTickPromptTemplate) &&
                payload.buyerTickPromptTemplate !==
                  DEFAULT_BUYER_TICK_PROMPT_TEMPLATE;
              const sellerLabel = !payload.sellerTickPromptTemplate
                ? "built-in default"
                : sellerEdited
                  ? 'edited'
                  : 'prefilled, unedited';
              const buyerLabel = !payload.buyerTickPromptTemplate
                ? "built-in default"
                : buyerEdited
                  ? 'edited'
                  : 'prefilled, unedited';
              return `Seller: ${sellerLabel} — Buyer: ${buyerLabel}`;
            })()}
          </Typography>
        </ReviewRow>
        <ReviewRow label="Tools">
          {payload.enabledTools.length === 0 ? (
            <Typography variant={'body2'}>All tools</Typography>
          ) : (
            <Box display={'flex'} flexWrap={'wrap'} gap={0.5}>
              {payload.enabledTools.map((name) => (
                <Chip
                  key={name}
                  size="small"
                  label={name}
                  variant={
                    ALWAYS_ON_TOOLS.includes(name) ? 'filled' : 'outlined'
                  }
                  color={
                    tools.find((t) => t?.name === name)?.source === 'existing'
                      ? 'warning'
                      : 'default'
                  }
                />
              ))}
            </Box>
          )}
        </ReviewRow>
        {submitError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        ) : null}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Typography variant={'h6'} fontWeight={'bold'}>
            Create Simulation
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={submitting}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 2 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minHeight: 420 }}>
        {activeStep === 0 ? (
          renderOrgStep()
        ) : activeStep === 1 ? (
          renderSellerStep()
        ) : activeStep === 2 ? (
          renderBuyerStep()
        ) : activeStep === 3 ? (
          <ToolsPicker
            tools={tools}
            fetching={fetchingTools}
            error={toolsError}
            restrict={restrictTools}
            onRestrictChange={setRestrictTools}
            selected={selectedTools}
            onSelectedChange={setSelectedTools}
          />
        ) : (
          renderReviewStep()
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={activeStep === 0 || submitting}
          onClick={() => setActiveStep((step) => step - 1)}
        >
          Back
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button
            size="small"
            variant="contained"
            disabled={!canGoNext}
            onClick={handleNext}
          >
            Next
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <>
                Creating...
                <CircularProgress size={16} sx={{ ml: 1 }} />
              </>
            ) : (
              'Create Simulation'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateSimulationDialog;
